import { addHours, isAfter } from 'date-fns';
import groupsService from 'packages/services/groupsService';
import fontsService from 'packages/services/fontsService';
import trackingService from 'packages/services/trackingService';
import userService from 'packages/services/userService';
import authService from 'packages/services/authService';
import featureTreatmentService from 'packages/services/featureTreatmentService';
import filtersStorage from 'packages/hooks/filtersStorage';
import { catchResponseError } from 'packages/helpers/helpers';
import { parseUrl } from 'packages/helpers/urlUtils';
import AuthorizationHelper from 'packages/helpers/AuthorizationHelper';
import isInIframe from 'packages/helpers/iframeHelper';
import ProxyServerConnector from 'packages/services/connectors/ProxyServerConnector';
import { updateEditorFonts } from 'packages/store/creativeEditor/creativeEditor.actions';
import * as types from 'packages/store/types';

const { syncForUser, getFiltersFromStorage, setFiltersToStorage } = filtersStorage();

let refetchFontsIntervalId: any = -1;
let visibilityChangeHandler: any = null;

export function getUser(): any {
    return async (dispatch) => {
        try {
            const { status, json: data } = (await userService.getUser()) as any;

            if (status === 200 && data) {
                AuthorizationHelper.setPermissions(data.permissions, data.company);
                AuthorizationHelper.setRoles(data.roles);
                AuthorizationHelper.setFeatures(data.accessibleFeatures);

                const currentCompanyData = data.companies.find((company) => company.id === data.company);
                AuthorizationHelper.appcuesIdentify(data.user, currentCompanyData, data.roles, data.accessibleFeatures);
                syncForUser(data.user.bynderUserId || data.user.id);

                dispatch(getUserFonts(data.company));

                dispatch({
                    type: types.LOGIN_SUCCESS,
                    user: data.user,
                    companies: data.companies,
                    currentCompany: data.company,
                    permissions: data.permissions,
                    roles: data.roles,
                    accessibleFeatures: data.accessibleFeatures,
                    bynderScopes: data.bynderScopes,
                });

                dispatch({
                    type: types.TOGGLE_APP_INITIALIZING,
                    state: false,
                });

                const storedUser = getFiltersFromStorage('loginTracking');

                if (storedUser) {
                    if (isAfter(new Date(), addHours(new Date(storedUser.currentDateTime), 1))) {
                        await trackingService.registerLogin({
                            bynderUserId: data.user.bynderUserId,
                            bynderAccountId: data.companies[0].bynderAccountId,
                        });

                        setFiltersToStorage('loginTracking', {
                            bynderUserId: data.user.bynderUserId,
                            bynderAccountId: data.companies[0].bynderAccountId,
                            currentDateTime: new Date(),
                        } as any);
                    }
                } else {
                    await trackingService.registerLogin({
                        bynderUserId: data.user.bynderUserId,
                        bynderAccountId: data.companies[0].bynderAccountId,
                    });

                    setFiltersToStorage('loginTracking', {
                        bynderUserId: data.user.bynderUserId,
                        bynderAccountId: data.companies[0].bynderAccountId,
                        currentDateTime: new Date(),
                    } as any);
                }

                dispatch(getGroups());
            }
        } catch (err) {
            catchResponseError(err);
        }
    };
}

export function loginBynderUser(): any {
    return async (dispatch, getState) => {
        const { portalDomain, bynderAccessToken } = getState().platform;

        if (!bynderAccessToken) {
            console.error('loginBynderUser: No bynder access token.');

            return;
        }

        try {
            const authResponse: any = await authService.exchangeBynderToIdentityToken({
                portalDomain,
                accessToken: bynderAccessToken,
            });

            if (authResponse.status === 200) {
                if (authResponse.json.loginUrl) {
                    if (location.pathname !== '/bynder-auth') {
                        const redirectUrl = `${location.pathname}${location.search}`;
                        console.warn('Set url on', redirectUrl);
                        sessionStorage.setItem('redirectUrl', redirectUrl);
                    }

                    if (isInIframe()) {
                        dispatch(sendRedirectCommand(authResponse.json.loginUrl));
                    } else {
                        window.location.href = authResponse.json.loginUrl;
                    }

                    return;
                }

                dispatch({
                    type: types.UPDATE_PLATFORM_OPTIONS,
                    options: {
                        accessToken: authResponse.json.identityAccessToken,
                    },
                });

                // TEMPORARY: Check if Kong gateway should be used (internal-api to kong-api migration)
                await dispatch(fetchKongGatewayFeatureTreatment());

                dispatch(getUser());

                ProxyServerConnector.flushWaitList();
            }
        } catch (err) {
            catchResponseError(err);
        }
    };
}

export function registerBynderUser(authCode, state, cb): any {
    return async (dispatch) => {
        dispatch({
            type: types.UPDATE_PLATFORM_OPTIONS,
            options: {
                bynderAccessToken: null,
            },
        });

        try {
            const {
                json: { bynderAccessToken },
                status,
            } = (await authService.registerBynderUser({ authCode, state })) as any;

            if (status === 200) {
                dispatch({
                    type: types.UPDATE_PLATFORM_OPTIONS,
                    options: {
                        bynderAccessToken,
                    },
                });

                if (cb) {
                    cb();
                }
            }
        } catch (err) {
            catchResponseError(err);
        }
    };
}

function getGroups() {
    return (dispatch) => {
        dispatch({ type: types.GET_GROUPS_REQUEST });
        groupsService
            .getGroups()
            .then(({ json }: any) => {
                dispatch({ type: types.GET_GROUPS_SUCCESS, groups: json.items });
            })
            .catch(catchResponseError);
    };
}

export function getUserFonts(currentCompanyId): any {
    return (dispatch) => {
        if (visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', visibilityChangeHandler);
        }

        clearInterval(refetchFontsIntervalId);

        dispatch({ type: types.GET_FONTS_REQUEST });

        const updateFonts = () =>
            fontsService
                .getFonts(currentCompanyId)
                .then(({ json }) => {
                    dispatch({ type: types.GET_FONTS_SUCCESS, items: json });
                    dispatch(updateEditorFonts());
                })
                .catch(catchResponseError);

        updateFonts();
        refetchFontsIntervalId = setInterval(updateFonts, 1000 * 60 * 10);

        // save callback in the upper scope to be able to remove when the function is called again
        visibilityChangeHandler = () => {
            if (document.hidden) {
                // Clear interval when tab becomes inactive
                clearInterval(refetchFontsIntervalId);
            } else {
                // Refetch fonts and create a new interval when tab becomes active
                updateFonts();
                refetchFontsIntervalId = setInterval(updateFonts, 1000 * 60 * 10);
            }
        };

        document.addEventListener('visibilitychange', visibilityChangeHandler);
    };
}

export function logout(): any {
    return (dispatch, getState) => {
        getState().platform.hostPort.postMessage({
            type: 'logout',
        });
        // clearAllFiltersFromStorage();

        dispatch({ type: types.LOGOUT });
    };
}

export function sendRefreshCommand(): any {
    return (dispatch, getState) => {
        getState().platform.hostPort.postMessage({
            type: 'refresh_token',
        });
        setFiltersToStorage('loginTracking', {
            ...getFiltersFromStorage('loginTracking'),
            currentDateTime: new Date(),
        });
    };
}

function sendRedirectCommand(url) {
    return (dispatch, getState) => {
        getState().platform.hostPort.postMessage({
            type: 'redirect',
            url,
        });
    };
}

/**
 * TEMPORARY: This function is used for testing the migration from internal-api to kong-api.
 * It fetches the 'studio_kong_gateway' feature flag to determine whether to route
 * requests through the Kong gateway. This should be removed once the migration is complete.
 */
export function fetchKongGatewayFeatureTreatment(): any {
    return async (dispatch, getState) => {
        const featureName = 'studio_kong_gateway';
        const { portalDomain, bynderAccessToken } = getState().platform;

        if (!bynderAccessToken) {
            console.error('fetchKongGatewayFeatureTreatment: No bynder access token.');

            return;
        }

        try {
            const treatment = await featureTreatmentService.fetchTreatment(
                portalDomain,
                bynderAccessToken,
                featureName,
            );

            dispatch({ type: types.SET_SPLIT_FEATURES, featureName, featureValue: treatment });

            if (treatment === 'on') {
                ProxyServerConnector.setGatewayPrefix(`${parseUrl(portalDomain)}/v7/studio`);
            }
        } catch (error) {
            console.error('fetchKongGatewayFeatureTreatment: Failed to fetch treatment for', featureName, error);
        }
    };
}
